import { POSTemplate } from '@retailsync/shared-types'

// ═══════════════════════════════════════════════════════════════════════════
// RetailsyncGo Native Templates — Matches tenant-admin OnboardingWizard
// These are the canonical CSV formats for importing data into RetailsyncGo.
// ═══════════════════════════════════════════════════════════════════════════

const RETAILSYNC_PRODUCTS_TEMPLATE: POSTemplate = {
  id: 'retailsync-products',
  name: 'RetailsyncGo — Products (Full)',
  format: 'csv',
  description: 'Import products with all RetailsyncGo fields — pharmacy/retail ready',
  entity: 'products',
  defaultMappings: [
    { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
    { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
    { sourceField: 'description', targetField: 'description', transform: 'string' },
    { sourceField: 'barcode', targetField: 'barcode', transform: 'string' },
    { sourceField: 'category', targetField: 'category', transform: 'string', required: true },
    { sourceField: 'brand', targetField: 'brand', transform: 'string' },
    { sourceField: 'unit', targetField: 'unit', transform: 'string' },
    { sourceField: 'department', targetField: 'department', transform: 'string' },
    { sourceField: 'price', targetField: 'price', transform: 'currency', required: true },
    { sourceField: 'cost_price', targetField: 'cost_price', transform: 'currency' },
    { sourceField: 'wholesale_price', targetField: 'wholesale_price', transform: 'currency' },
    { sourceField: 'bulk_min_quantity', targetField: 'bulk_min_quantity', transform: 'number' },
    { sourceField: 'bulk_price', targetField: 'bulk_price', transform: 'currency' },
    { sourceField: 'sell_by', targetField: 'sell_by', transform: 'string' },
    // `stock_quantity` is the canonical name: the cloud's `products.in_stock` is a BOOLEAN, so a
    // count filed there is rejected. `in_stock` still works as an accepted alias on the way in.
    { sourceField: 'stock_quantity', targetField: 'stock_quantity', transform: 'number', required: true },
    { sourceField: 'min_stock_level', targetField: 'min_stock_level', transform: 'number' },
    { sourceField: 'max_stock_level', targetField: 'max_stock_level', transform: 'number' },
    { sourceField: 'is_active', targetField: 'is_active', transform: 'boolean' },
    { sourceField: 'show_online', targetField: 'show_online', transform: 'boolean' },
    { sourceField: 'item_type', targetField: 'item_type', transform: 'string' },
    { sourceField: 'requires_prescription', targetField: 'requires_prescription', transform: 'boolean' },
    { sourceField: 'expiry_date', targetField: 'expiry_date', transform: 'date' },
    { sourceField: 'batch_number', targetField: 'batch_number', transform: 'string' },
    { sourceField: 'manufacturer', targetField: 'manufacturer', transform: 'string' },
  ],
}

const RETAILSYNC_CUSTOMERS_TEMPLATE: POSTemplate = {
  id: 'retailsync-customers',
  name: 'RetailsyncGo — Customers',
  format: 'csv',
  description: 'Import customer profiles with full details',
  entity: 'customers',
  defaultMappings: [
    { sourceField: 'full_name', targetField: 'name', transform: 'string', required: true },
    { sourceField: 'email', targetField: 'email', transform: 'string', required: true },
    { sourceField: 'phone', targetField: 'phone', transform: 'string', required: true },
    { sourceField: 'customer_type', targetField: 'customer_type', transform: 'string' },
    { sourceField: 'address', targetField: 'address', transform: 'string' },
    { sourceField: 'city', targetField: 'city', transform: 'string' },
    { sourceField: 'state', targetField: 'state', transform: 'string' },
    { sourceField: 'postal_code', targetField: 'postal_code', transform: 'string' },
    { sourceField: 'country', targetField: 'country', transform: 'string' },
    { sourceField: 'date_of_birth', targetField: 'date_of_birth', transform: 'date' },
    { sourceField: 'gender', targetField: 'gender', transform: 'string' },
    { sourceField: 'is_active', targetField: 'is_active', transform: 'boolean' },
    { sourceField: 'notes', targetField: 'notes', transform: 'string' },
  ],
}

const RETAILSYNC_INVENTORY_TEMPLATE: POSTemplate = {
  id: 'retailsync-inventory',
  name: 'RetailsyncGo — Inventory (Branch)',
  format: 'csv',
  description: 'Import branch-level inventory with warehouse zones',
  entity: 'inventory',
  defaultMappings: [
    { sourceField: 'product_sku', targetField: 'product_sku', transform: 'string', required: true },
    { sourceField: 'branch_name', targetField: 'branch_id', transform: 'branch_resolve', required: true },
    { sourceField: 'warehouse_name', targetField: 'warehouse_id', transform: 'string' },
    { sourceField: 'quantity', targetField: 'quantity', transform: 'number', required: true },
    { sourceField: 'reserved_quantity', targetField: 'reserved_quantity', transform: 'number' },
    { sourceField: 'min_stock_level', targetField: 'min_stock_level', transform: 'number' },
    { sourceField: 'max_stock_level', targetField: 'max_stock_level', transform: 'number' },
    // The live column is `inventory.location` — `location_zone` does not exist.
    { sourceField: 'location', targetField: 'location', transform: 'string' },
  ],
}

const RETAILSYNC_STAFF_TEMPLATE: POSTemplate = {
  id: 'retailsync-staff',
  name: 'RetailsyncGo — Staff',
  format: 'csv',
  description: 'Import staff users for POS and branch access',
  entity: 'staff',
  defaultMappings: [
    { sourceField: 'email', targetField: 'email', transform: 'string', required: true },
    { sourceField: 'full_name', targetField: 'name', transform: 'string', required: true },
    { sourceField: 'role', targetField: 'role', transform: 'string', required: true },
    { sourceField: 'password', targetField: 'password', transform: 'string' },
    { sourceField: 'phone', targetField: 'phone', transform: 'string' },
    { sourceField: 'branch_name', targetField: 'branch_id', transform: 'branch_resolve' },
    { sourceField: 'department', targetField: 'department', transform: 'string' },
    // The live column is `staff.employee_code` (20260624000014_); `employee_id` is not a column.
    { sourceField: 'employee_code', targetField: 'employee_code', transform: 'string' },
    { sourceField: 'is_active', targetField: 'is_active', transform: 'boolean' },
    // `employment_status` is not a cloud column and its values ('engaged') are not dates. The
    // real column is `staff.hire_date`.
    { sourceField: 'hire_date', targetField: 'hire_date', transform: 'date' },
  ],
}

const RETAILSYNC_SUPPLIERS_TEMPLATE: POSTemplate = {
  id: 'retailsync-suppliers',
  name: 'RetailsyncGo — Suppliers',
  format: 'csv',
  description: 'Import supplier profiles with payment terms',
  entity: 'suppliers',
  defaultMappings: [
    { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
    { sourceField: 'contact_person', targetField: 'contact_person', transform: 'string' },
    { sourceField: 'email', targetField: 'email', transform: 'string' },
    { sourceField: 'phone', targetField: 'phone', transform: 'string' },
    { sourceField: 'address', targetField: 'address', transform: 'string' },
    { sourceField: 'tax_id', targetField: 'tax_id', transform: 'string' },
    { sourceField: 'payment_terms', targetField: 'payment_terms', transform: 'string' },
    { sourceField: 'credit_limit', targetField: 'credit_limit', transform: 'currency' },
    { sourceField: 'is_active', targetField: 'is_active', transform: 'boolean' },
    { sourceField: 'notes', targetField: 'notes', transform: 'string' },
  ],
}

const RETAILSYNC_CUSTOMER_LEDGER_TEMPLATE: POSTemplate = {
  id: 'retailsync-customer-ledger',
  name: 'RetailsyncGo — Customer Ledger',
  format: 'csv',
  description: 'Import customer account entries (credits, debits, payments)',
  entity: 'customer_ledger',
  defaultMappings: [
    { sourceField: 'customer_email', targetField: 'customer_email', transform: 'string', required: true },
    { sourceField: 'transaction_type', targetField: 'transaction_type', transform: 'string', required: true },
    { sourceField: 'amount', targetField: 'amount', transform: 'currency', required: true },
    { sourceField: 'reference', targetField: 'reference', transform: 'string' },
    { sourceField: 'transaction_date', targetField: 'transaction_date', transform: 'date' },
    { sourceField: 'notes', targetField: 'notes', transform: 'string' },
  ],
}

const RETAILSYNC_SUPPLIER_LEDGER_TEMPLATE: POSTemplate = {
  id: 'retailsync-supplier-ledger',
  name: 'RetailsyncGo — Supplier Ledger',
  format: 'csv',
  description: 'Import supplier account entries (purchases, payments)',
  entity: 'supplier_ledger',
  defaultMappings: [
    { sourceField: 'supplier_name', targetField: 'supplier_name', transform: 'string', required: true },
    { sourceField: 'transaction_type', targetField: 'transaction_type', transform: 'string', required: true },
    { sourceField: 'amount', targetField: 'amount', transform: 'currency', required: true },
    { sourceField: 'reference', targetField: 'reference', transform: 'string' },
    { sourceField: 'transaction_date', targetField: 'transaction_date', transform: 'date' },
    { sourceField: 'notes', targetField: 'notes', transform: 'string' },
  ],
};

export const POS_TEMPLATES: POSTemplate[] = [
  RETAILSYNC_PRODUCTS_TEMPLATE,
  RETAILSYNC_CUSTOMERS_TEMPLATE,
  RETAILSYNC_INVENTORY_TEMPLATE,
  RETAILSYNC_STAFF_TEMPLATE,
  RETAILSYNC_SUPPLIERS_TEMPLATE,
  RETAILSYNC_CUSTOMER_LEDGER_TEMPLATE,
  RETAILSYNC_SUPPLIER_LEDGER_TEMPLATE,
  {
    id: 'square-products',
    name: 'Square POS - Products',
    format: 'csv',
    description: 'Import products from Square POS export',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'item name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'quantity', targetField: 'stock', transform: 'number' },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'upc', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'shopify-products',
    name: 'Shopify - Products',
    format: 'csv',
    description: 'Import products from Shopify CSV export',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'title', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'variant sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'product type', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'variant price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'variant inventory qty', targetField: 'stock', transform: 'number' },
      { sourceField: 'body (html)', targetField: 'description', transform: 'string' },
      { sourceField: 'variant barcode', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'quickbooks-products',
    name: 'QuickBooks - Items',
    format: 'excel',
    description: 'Import items from QuickBooks export',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'type', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'sales price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'quantity on hand', targetField: 'stock', transform: 'number' },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'barcode', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'square-transactions',
    name: 'Square POS - Transactions',
    format: 'csv',
    description: 'Import transactions from Square POS export',
    entity: 'transactions',
    defaultMappings: [
      { sourceField: 'date', targetField: 'date', transform: 'date', required: true },
      { sourceField: 'time', targetField: 'time', transform: 'string' },
      { sourceField: 'item', targetField: 'items', transform: 'string', required: true },
      { sourceField: 'qty', targetField: 'quantity', transform: 'number' },
      { sourceField: 'price point name', targetField: 'price_point', transform: 'string' },
      { sourceField: 'gross sales', targetField: 'subtotal', transform: 'currency' },
      { sourceField: 'discounts', targetField: 'discount', transform: 'currency' },
      { sourceField: 'net sales', targetField: 'total', transform: 'currency', required: true },
      { sourceField: 'tax', targetField: 'tax', transform: 'currency' },
      { sourceField: 'transaction id', targetField: 'id', transform: 'string' },
      { sourceField: 'payment id', targetField: 'payment_id', transform: 'string' },
      { sourceField: 'card brand', targetField: 'payment_method', transform: 'string' },
      { sourceField: 'customer name', targetField: 'customer_name', transform: 'string' },
      { sourceField: 'customer id', targetField: 'customer_id', transform: 'string' }
    ]
  },
  {
    id: 'generic-csv-products',
    name: 'Generic CSV - Products',
    format: 'csv',
    description: 'Generic product import from CSV file',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'product_name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'product_sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'product_category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'product_price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'product_cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'stock', targetField: 'stock', transform: 'number' },
      { sourceField: 'quantity', targetField: 'stock', transform: 'number' },
      { sourceField: 'inventory', targetField: 'stock', transform: 'number' },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'product_description', targetField: 'description', transform: 'string' },
      { sourceField: 'barcode', targetField: 'barcode', transform: 'string' },
      { sourceField: 'upc', targetField: 'barcode', transform: 'string' },
      { sourceField: 'ean', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'generic-excel-products',
    name: 'Generic Excel - Products',
    format: 'excel',
    description: 'Generic product import from Excel file',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'product name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'product sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'product category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'product price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'product cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'stock', targetField: 'stock', transform: 'number' },
      { sourceField: 'quantity', targetField: 'stock', transform: 'number' },
      { sourceField: 'inventory', targetField: 'stock', transform: 'number' },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'product description', targetField: 'description', transform: 'string' },
      { sourceField: 'barcode', targetField: 'barcode', transform: 'string' },
      { sourceField: 'upc', targetField: 'barcode', transform: 'string' },
      { sourceField: 'ean', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'generic-json-products',
    name: 'Generic JSON - Products',
    format: 'json',
    description: 'Generic product import from JSON file',
    entity: 'products',
    defaultMappings: [
      { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'productName', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'sku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'productSku', targetField: 'sku', transform: 'string', required: true },
      { sourceField: 'category', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'productCategory', targetField: 'category', transform: 'string', required: true },
      { sourceField: 'price', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'productPrice', targetField: 'price', transform: 'currency', required: true },
      { sourceField: 'cost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'productCost', targetField: 'cost', transform: 'currency' },
      { sourceField: 'stock', targetField: 'stock', transform: 'number' },
      { sourceField: 'quantity', targetField: 'stock', transform: 'number' },
      { sourceField: 'inventory', targetField: 'stock', transform: 'number' },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'productDescription', targetField: 'description', transform: 'string' },
      { sourceField: 'barcode', targetField: 'barcode', transform: 'string' },
      { sourceField: 'upc', targetField: 'barcode', transform: 'string' },
      { sourceField: 'ean', targetField: 'barcode', transform: 'string' }
    ]
  },
  {
    id: 'retailsync-transactions',
    name: 'RetailsyncGo — Transactions',
    format: 'csv',
    description: 'Import sales transactions — dry-run supported',
    entity: 'transactions',
    defaultMappings: [
      { sourceField: 'product_sku', targetField: 'product_sku', transform: 'string', required: true },
      { sourceField: 'quantity', targetField: 'quantity', transform: 'number', required: true },
      { sourceField: 'unit_price', targetField: 'unit_price', transform: 'currency' },
      { sourceField: 'total', targetField: 'total', transform: 'currency' },
      { sourceField: 'payment_method', targetField: 'payment_method', transform: 'string' },
      { sourceField: 'customer_email', targetField: 'customer_email', transform: 'string' },
      { sourceField: 'timestamp', targetField: 'timestamp', transform: 'date' },
      { sourceField: 'external_id', targetField: 'external_id', transform: 'string' },
      { sourceField: 'notes', targetField: 'notes', transform: 'string' },
    ]
  },
  // ═══════════════════════════════════════════════════════════
  // Extended entity templates — purchase_orders, recipes, production_batches
  // ═══════════════════════════════════════════════════════════
  {
    id: 'retailsync-purchase-orders',
    name: 'RetailsyncGo — Purchase Orders',
    format: 'csv',
    description: 'Import supplier purchase orders with line items',
    entity: 'purchase_orders',
    defaultMappings: [
      { sourceField: 'supplier_name', targetField: 'supplier_id', transform: 'supplier_resolve', required: true },
      { sourceField: 'order_number', targetField: 'order_number', transform: 'string', required: true },
      { sourceField: 'status', targetField: 'status', transform: 'string' },
      { sourceField: 'order_date', targetField: 'order_date', transform: 'date', required: true },
      { sourceField: 'expected_delivery_date', targetField: 'expected_delivery_date', transform: 'date' },
      { sourceField: 'product_sku', targetField: 'product_sku', transform: 'string', required: true },
      { sourceField: 'quantity', targetField: 'quantity', transform: 'number', required: true },
      { sourceField: 'unit_price', targetField: 'unit_price', transform: 'currency', required: true },
      { sourceField: 'total_amount', targetField: 'total_amount', transform: 'currency' },
      { sourceField: 'notes', targetField: 'notes', transform: 'string' },
    ],
  },
  {
    id: 'retailsync-recipes',
    name: 'RetailsyncGo — Recipes',
    format: 'csv',
    description: 'Import production recipes with ingredient lists',
    entity: 'recipes',
    defaultMappings: [
      { sourceField: 'name', targetField: 'name', transform: 'string', required: true },
      { sourceField: 'description', targetField: 'description', transform: 'string' },
      { sourceField: 'output_product_sku', targetField: 'output_product_id', transform: 'product_resolve', required: true },
      { sourceField: 'output_quantity', targetField: 'output_quantity', transform: 'number', required: true },
      { sourceField: 'unit', targetField: 'unit', transform: 'string' },
      { sourceField: 'labor_cost', targetField: 'labor_cost', transform: 'currency' },
      { sourceField: 'overhead_cost', targetField: 'overhead_cost', transform: 'currency' },
      { sourceField: 'ingredients_json', targetField: 'ingredients', transform: 'string' },
      { sourceField: 'status', targetField: 'status', transform: 'string' },
    ],
  },
  {
    id: 'retailsync-production-batches',
    name: 'RetailsyncGo — Production Batches',
    format: 'csv',
    description: 'Import batch production runs with yield tracking',
    entity: 'production_batches',
    defaultMappings: [
      { sourceField: 'recipe_name', targetField: 'recipe_id', transform: 'recipe_resolve', required: true },
      { sourceField: 'batch_number', targetField: 'batch_number', transform: 'string', required: true },
      { sourceField: 'planned_quantity', targetField: 'planned_quantity', transform: 'number', required: true },
      { sourceField: 'actual_quantity', targetField: 'actual_quantity', transform: 'number' },
      { sourceField: 'waste_quantity', targetField: 'waste_quantity', transform: 'number' },
      { sourceField: 'status', targetField: 'status', transform: 'string' },
      { sourceField: 'start_date', targetField: 'start_date', transform: 'date' },
      { sourceField: 'completion_date', targetField: 'completion_date', transform: 'date' },
      { sourceField: 'notes', targetField: 'notes', transform: 'string' },
    ],
  },
]

// Helper function to get templates by entity
export function getTemplatesByEntity(entity: string): POSTemplate[] {
  return POS_TEMPLATES.filter(template => template.entity === entity)
}

// Helper function to get template by ID
export function getTemplateById(id: string): POSTemplate | undefined {
  return POS_TEMPLATES.find(template => template.id === id)
}

// Helper function to get templates by format
export function getTemplatesByFormat(format: string): POSTemplate[] {
  return POS_TEMPLATES.filter(template => template.format === format)
}